import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { CompanySwitcher } from "./CompanySwitcher";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  CalendarCheck,
  PackageOpen,
  UserCircle,
  Warehouse,
  FileBarChart,
  Settings,
  Shield,
  User,
  LogOut,
  Building2,
} from "lucide-react";

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  key: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", key: "dashboard" },
  { icon: CalendarCheck, label: "Bokningar", path: "/bookings", key: "bookings" },
  { icon: PackageOpen, label: "Däck", path: "/tires", key: "tires" },
  { icon: UserCircle, label: "Kunder", path: "/customers", key: "customers" },
  { icon: Warehouse, label: "Lager", path: "/storage", key: "storage" },
  { icon: Building2, label: "Företag", path: "/companies", key: "companies", adminOnly: true },
  { icon: FileBarChart, label: "Rapporter", path: "/reports", key: "reports" },
];

export default function UserSidebar() {
  const { user, signOut } = useAuth();
  const { isSystemOwner, isAdmin, isCompanyOwner } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: profile } = useQuery({
    queryKey: ["sidebar-profile", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, email")
        .eq("id", user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const displayName = profile?.first_name || user?.email?.split("@")[0] || "?";

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const filteredItems = navItems.filter((item) => {
    if (item.adminOnly && !isSystemOwner && !isAdmin && !isCompanyOwner) return false;
    return true;
  });

  return (
    <aside className="fixed left-0 top-14 h-[calc(100vh-3.5rem)] w-[88px] bg-card border-r border-border z-40 hidden md:flex flex-col">
      {/* Company Switcher */}
      <div className="px-2 pt-3 pb-2 border-b border-border">
        <CompanySwitcher />
      </div>

      {/* Navigation */}
      <nav className="flex-1 pt-3 pb-2 px-2 space-y-1 overflow-y-auto noscroll">
        {filteredItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Tooltip key={item.key}>
              <TooltipTrigger asChild>
                <button
                  data-tour={`nav-${item.key}`}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "relative flex flex-col items-center justify-center gap-1 w-full py-2.5 rounded-xl transition-all duration-200 group focusable",
                    active
                      ? "bg-accent-soft text-accent-ink"
                      : "text-foreground-muted hover:bg-background-2 hover:text-foreground"
                  )}
                >
                  {active && (
                    <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-accent" />
                  )}
                  <Icon className={cn("h-[22px] w-[22px]", active && "text-accent")} strokeWidth={active ? 2.2 : 1.8} />
                  <span className="text-[10px] font-semibold leading-tight max-w-full truncate px-1">
                    {item.label}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{item.label}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </nav>

      {/* System Admin */}
      {(isSystemOwner || isAdmin) && (
        <div className="px-2 pb-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => navigate("/admin")}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 w-full py-2.5 rounded-xl transition-all duration-200 focusable",
                  location.pathname.startsWith("/admin")
                    ? "bg-terra-soft text-terra-ink"
                    : "text-foreground-muted hover:bg-terra-soft hover:text-terra-ink"
                )}
              >
                <Shield className="h-[22px] w-[22px]" strokeWidth={1.8} />
                <span className="text-[10px] font-semibold">Admin</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right"><p>Systemadministration</p></TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* User */}
      <div className="p-2 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex flex-col items-center justify-center gap-1 w-full py-2 rounded-xl transition-all duration-200 hover:bg-background-2 text-foreground-muted hover:text-foreground focusable">
              <div className="w-9 h-9 rounded-full bg-accent-soft text-accent-ink border border-border flex items-center justify-center font-display font-semibold text-sm">
                {displayName.slice(0, 1).toUpperCase()}
              </div>
              <span className="text-[10px] font-semibold leading-tight truncate max-w-full px-1">
                {displayName.length > 8 ? displayName.slice(0, 7) + "." : displayName}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="w-52">
            <div className="px-3 py-2 border-b border-border">
              <p className="text-sm font-semibold truncate">
                {profile ? `${profile.first_name} ${profile.last_name}`.trim() : displayName}
              </p>
              <p className="text-xs text-foreground-muted truncate">{user?.email}</p>
            </div>
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="mr-2 h-4 w-4" /> Profilinställningar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => signOut()}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" /> Logga ut
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
